import React, { useRef, useState } from "react";
import { Callout, DirectionalHint } from "@fluentui/react/lib/Callout";
import { edcDateFormat } from "maven-lib/dist/consts/edc/lib";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrInput, CrInputProps } from "./CrInput";
import { CrYearExt } from "./CrYearExt";

export interface CrYearDateLabel {
  year?: string;
}

export interface CrYearInputProps extends Omit<CrInputProps, "defaultValue" | "value" | "iconName"> {
  minDate?: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  defaultValue?: string | Date;
  value?: string | Date;
  dateLabel?: CrYearDateLabel;
  onDeleteDate?: () => void;
  allUnknowable: boolean;
  yearUnknowable: boolean;
}

export function CrYearInput(props: CrYearInputProps) {
  const dummyInputRef = useRef<HTMLInputElement>(null);
  const calendarInputRef = useRef<HTMLDivElement>(null);

  const {
    maxDate,
    minDate,
    defaultValue: defaultValueO,
    value: valueO,
    onValueChange,
    readonly,
    onDeleteDate,
    dateLabel,
    allUnknowable,
    yearUnknowable,
    ...extras
  } = props;
  const [showYearInput, setShowYearInput] = useState(false);

  const [controlledValue, setControlledValue] = useState<string>(() =>
    ((defaultValue, value) => {
      if (typeof value === "string") return edcDateFormat(value, "YYYY");
      else if (typeof defaultValue === "string") return edcDateFormat(defaultValue, "YYYY");
      else return "";
    })(defaultValueO, valueO)
  );

  const dismissYearCallout = () => {
    setShowYearInput(false);
    dummyInputRef.current?.focus();
  };

  return (
    <div className={classNames("cr-calendar-input", showYearInput && "is-active")}>
      <div ref={calendarInputRef}>
        <CrInput
          value={controlledValue}
          onFocus={() => !readonly && setShowYearInput(true)}
          readonly={true}
          iconName="Calendar"
          {...extras}
        />
      </div>
      <input
        tabIndex={-1}
        ref={dummyInputRef}
        readOnly
        style={{ position: "absolute", top: "0", right: "0", width: "1px", height: "1px", opacity: 0.0001, zIndex: 0 }}
      />
      {showYearInput && (
        <>
          <Callout
            isBeakVisible={true}
            className="ms-DatePicker-callout cr-calendar-callout"
            gapSpace={0}
            doNotLayer={false}
            target={calendarInputRef.current?.querySelector(".ms-TextField-fieldGroup")}
            directionalHint={DirectionalHint.bottomCenter}
            onDismiss={dismissYearCallout}
          >
            <CrYearExt
              maxDate={maxDate}
              minDate={minDate}
              selectedYear={parseInt(controlledValue.slice(0, 4), 10) || undefined}
              dateLabel={dateLabel}
              allUnknowable={allUnknowable}
              yearUnknowable={yearUnknowable}
              onDismiss={dismissYearCallout}
              onValueChange={(vv) => {
                const v = vv.slice(0, 4);

                onValueChange && onValueChange(v);
                setControlledValue(v);
                dismissYearCallout();
              }}
              onDeleteDate={() => {
                onDeleteDate && onDeleteDate();
                setControlledValue("");
                dismissYearCallout();
              }}
            />
          </Callout>
        </>
      )}
    </div>
  );
}

CrYearInput.defaultProps = {
  maxDate: "TODAY",
  allUnknowable: false,
  yearUnknowable: false,
};
